// JIGS SFX Grading Scheme — disciplines, grades, qualifications and
// per-grade progression requirements.
//
// This is a pure constants module: no imports, no side effects. Consumers
// (e.g. utils/gradingStats.js, grading UI pages) import from here so the
// thresholds live in exactly one place.

/**
 * The four SFX disciplines a crew member can be graded in.
 * @type {Array<{ id: string, label: string }>}
 */
export const DISCIPLINES = [
  { id: 'physical', label: 'Physical SFX' },
  { id: 'pyrotechnic', label: 'Pyrotechnic SFX' },
  { id: 'visual', label: 'Visual SFX' },
  { id: 'snow', label: 'Snow SFX' },
];

/** Map of discipline id -> display label. */
export const DISCIPLINE_LABELS = DISCIPLINES.reduce((acc, d) => {
  acc[d.id] = d.label;
  return acc;
}, {});

/** Grades in ascending order of seniority. */
export const GRADE_ORDER = ['trainee', 'technician', 'senior_technician', 'supervisor'];

/** Map of grade id -> display label. */
export const GRADE_LABELS = {
  trainee: 'Trainee',
  technician: 'Technician',
  senior_technician: 'Senior Technician',
  supervisor: 'Supervisor',
};

/**
 * The five explosives qualifications referenced by the pyrotechnic discipline.
 * @type {Array<{ id: string, name: string }>}
 */
export const QUALIFICATIONS = [
  {
    id: 'basic_explosives_course',
    name: 'Attended approved course in Basic Explosives Safety and Handling',
  },
  {
    id: 'basic_explosives_written',
    name: 'Passed approved Basic Explosives Safety written assessment',
  },
  {
    id: 'iexpe_intermediate',
    name: 'Attended IExpE Intermediate Explosives Science and Legislation course',
  },
  {
    id: 'iexpe_general_papers',
    name: 'Passed all three IExpE General Papers examinations',
  },
  {
    id: 'iexpe_specialist_paper_8',
    name: 'Passed IExpE Specialist Paper No.8 - Special Effects',
  },
];

// Pyrotechnic-only qualification ladder — required at every pyro grade.
// Each grade carries the FULL cumulative set (not just the delta over the
// prior grade) so consumers can check membership without walking the chain.
const PYRO_TECH_QUALS = ['basic_explosives_course', 'basic_explosives_written'];
const PYRO_SENIOR_QUALS = [...PYRO_TECH_QUALS, 'iexpe_intermediate', 'iexpe_general_papers'];

// Supervisor qualification set — applies at the Supervisor grade across ALL
// disciplines. A Supervisor oversees multi-discipline shoots (including
// pyro), so JIGS requires the full explosives course + exam track at this
// grade even when the candidate's day-to-day work is Physical/Visual/Snow.
// Tech and Senior in non-pyro disciplines do NOT carry these — only the
// step up to Supervisor introduces them.
const SUPERVISOR_QUALS = [...PYRO_SENIOR_QUALS, 'iexpe_specialist_paper_8'];

// Shared trainee shape — entry grade, no requirements.
const traineeReqs = {
  minYears: 0,
  minWeeks: 0,
  minDays: 0,
  supervisorSignatures: 0,
  riskAssessments: 0,
  qualifications: [],
  progressiveGradeRequired: null,
  dualDiscipline: false,
};

/**
 * Per-discipline, per-grade progression requirements.
 *
 * Shape: GRADING_REQUIREMENTS[discipline][grade] = {
 *   minYears, minWeeks, minDays,
 *   supervisorSignatures, riskAssessments,
 *   qualifications: [qualId...],     // full cumulative list required at this grade
 *   progressiveGradeRequired,        // grade that must already be held, or null
 *   dualDiscipline                   // requires competence across two disciplines
 * }
 */
export const GRADING_REQUIREMENTS = {
  physical: {
    trainee: { ...traineeReqs },
    technician: {
      minYears: 5,
      minWeeks: 200,
      minDays: 1000,
      supervisorSignatures: 3,
      riskAssessments: 0,
      qualifications: [],
      progressiveGradeRequired: 'trainee',
      dualDiscipline: false,
    },
    senior_technician: {
      minYears: 10,
      minWeeks: 400,
      minDays: 2000,
      supervisorSignatures: 3,
      riskAssessments: 2,
      qualifications: [],
      progressiveGradeRequired: 'technician',
      dualDiscipline: false,
    },
    supervisor: {
      minYears: 15,
      minWeeks: 600,
      minDays: 3000,
      supervisorSignatures: 3,
      riskAssessments: 2,
      qualifications: [...SUPERVISOR_QUALS],
      progressiveGradeRequired: 'senior_technician',
      dualDiscipline: true,
    },
  },
  pyrotechnic: {
    trainee: { ...traineeReqs },
    technician: {
      minYears: 5,
      minWeeks: 50,
      minDays: 250,
      supervisorSignatures: 3,
      riskAssessments: 0,
      qualifications: [...PYRO_TECH_QUALS],
      progressiveGradeRequired: 'trainee',
      dualDiscipline: false,
    },
    senior_technician: {
      minYears: 10,
      minWeeks: 100,
      minDays: 500,
      supervisorSignatures: 3,
      riskAssessments: 2,
      qualifications: [...PYRO_SENIOR_QUALS],
      progressiveGradeRequired: 'technician',
      dualDiscipline: false,
    },
    supervisor: {
      minYears: 15,
      minWeeks: 150,
      minDays: 750,
      supervisorSignatures: 3,
      riskAssessments: 2,
      qualifications: [...SUPERVISOR_QUALS],
      progressiveGradeRequired: 'senior_technician',
      dualDiscipline: true,
    },
  },
  visual: {
    trainee: { ...traineeReqs },
    technician: {
      minYears: 5,
      minWeeks: 100,
      minDays: 500,
      supervisorSignatures: 3,
      riskAssessments: 0,
      qualifications: [],
      progressiveGradeRequired: 'trainee',
      dualDiscipline: false,
    },
    senior_technician: {
      minYears: 10,
      minWeeks: 200,
      minDays: 1000,
      supervisorSignatures: 3,
      riskAssessments: 2,
      qualifications: [],
      progressiveGradeRequired: 'technician',
      dualDiscipline: false,
    },
    supervisor: {
      minYears: 15,
      minWeeks: 300,
      minDays: 1500,
      supervisorSignatures: 3,
      riskAssessments: 2,
      qualifications: [...SUPERVISOR_QUALS],
      progressiveGradeRequired: 'senior_technician',
      dualDiscipline: false,
    },
  },
  snow: {
    trainee: { ...traineeReqs },
    technician: {
      minYears: 2,
      minWeeks: 80,
      minDays: 400,
      supervisorSignatures: 3,
      riskAssessments: 0,
      qualifications: [],
      progressiveGradeRequired: 'trainee',
      dualDiscipline: false,
    },
    senior_technician: {
      minYears: 4,
      minWeeks: 160,
      minDays: 800,
      supervisorSignatures: 3,
      riskAssessments: 2,
      qualifications: [],
      progressiveGradeRequired: 'technician',
      dualDiscipline: false,
    },
    supervisor: {
      minYears: 6,
      minWeeks: 240,
      minDays: 1200,
      supervisorSignatures: 3,
      riskAssessments: 2,
      qualifications: [...SUPERVISOR_QUALS],
      progressiveGradeRequired: 'senior_technician',
      dualDiscipline: false,
    },
  },
};
